import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { useActiveClientContext } from 'store/active-client';
import { getUserInfo } from 'utils/auth';

import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';

import MainLayout from 'components/main-layout/MainLayout';
import LayoutContent from 'components/main-layout/LayoutContent';
import TabNavigation from 'components/TabNavigation';
import NotAuthorizedScreen from 'routes/not-authorized';

interface IProps {
  isLoading?: boolean;
  error?: unknown;
  children: React.ReactNode;
}

const InnerWrapper: FC<IProps> = ({ isLoading, error, children }: IProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { client } = useActiveClientContext();

  const { featureWorkTrips } = getUserInfo();

  if (!featureWorkTrips) {
    return <NotAuthorizedScreen />;
  }

  const tabs = [
    {
      to: '/my-work-trips',
      label: t('workTrips.tabs.list'),
      entityName: buildClientEntity('my-work-trips', client),
      isExact: true,
    },
    {
      to: '/my-work-trips/completed',
      label: t('workTrips.tabs.completedList'),
      entityName: buildClientEntity('my-work-trips-completed', client),
      isExact: true,
    },
    {
      to: '/my-work-trips/calendar',
      label: t('workTrips.tabs.calendar'),
      entityName: buildClientEntity('my-work-trips-calendar', client),
      isExact: true,
    },
    {
      onRefresh: () => {
        const currentTab = tabs.find(tab => tab.to && buildClientUrl(tab.to, client) === location.pathname);
        if (currentTab?.entityName) {
          queryClient.resetQueries(currentTab.entityName);
        }
      },
    },
  ];

  return (
    <MainLayout title={t('workTrips.title')}>
      <TabNavigation tabs={tabs} />
      <LayoutContent
        isLoading={isLoading}
        error={error}
      >
        {children}
      </LayoutContent>
    </MainLayout>
  );
};

export default InnerWrapper;
